import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Loading } from './common/components/general/loading/loading';
import { Header } from './header/header';
import { Footer } from './footer/footer';
import { SubHeader } from './sub-header/sub-header';
import { filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CacheService } from './common/services/cache-service';
import { CACHE_KEYS } from './common/constants/cache-keys';
import { GigyaSessionService } from './common/services/gigya-session-service';
import { LoadingService } from './common/components/general/loading/loading-service';
import { AnalyticsService } from './common/services/analytics-service';
import { environment } from '../environments/environment';

@Component({
  selector: 'srp-root',
  imports: [CommonModule, RouterOutlet, Loading, Header, Footer, SubHeader, ScrollingModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'srp-app-wrapper' }
})
export class App implements OnInit, OnDestroy, AfterViewInit {
  protected title = 'srp-portal';
  showHeader: boolean = false;
  showFooter: boolean = false;
  showSubHeader: boolean = false;
  className: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly cacheService: CacheService,
    private readonly gigyaSessionService: GigyaSessionService,
    private readonly overlayContainer: OverlayContainer,
    private readonly loadingService: LoadingService,
    private readonly analyticsService: AnalyticsService
  ) { }

  ngAfterViewInit(): void {
    if(environment.NAME === 'dev') {
      [
        'https://universal-editor-service.adobe.io/cors.js',
        'https://universal-editor-service.adobe.io/static/canvas-client.js',
      ].forEach((source) => {
        if (!document.querySelector(`script[src="${source}"]`)) {
          const script = document.createElement('script');
          script.src = source;
          script.async = true;
          document.body.appendChild(script);
        }
      });
    }
  }
  
  ngOnInit(): void {
    // Show loader on navigation start
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadingService.start();
      });

    // Hide loader on navigation end, cancel, or error
    this.router.events
      .pipe(
        filter(event => 
          event instanceof NavigationEnd || 
          event instanceof NavigationCancel || 
          event instanceof NavigationError
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadingService.stop();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        let currentRoute = this.activatedRoute;
        while (currentRoute?.firstChild) {
          currentRoute = currentRoute?.firstChild;
        }
        const routeData = currentRoute.snapshot.data;

        // Default to true, but override if data is defined
        this.showHeader = !(routeData['hideHeader'] ?? false);
        this.showFooter = !(routeData['hideFooter'] ?? false);
        this.showSubHeader = !(routeData['hideSubHeader'] ?? false);

        //get user type from cache
        const isErcsUser = this.cacheService.getFromCache(CACHE_KEYS.USER.IS_ERCS);
        if (isErcsUser) {
          this.overlayContainer.getContainerElement().classList.add('user-erc');
        }

        this.className = isErcsUser ? 'app-container user-erc' : 'app-container';

        // Track virtual path only when pageCategory is explicitly configured on the route.
        const pageCategory = currentRoute.routeConfig?.data?.['pageCategory'];
        const disableAutoVirtualPath = !!currentRoute.routeConfig?.data?.['disableAutoVirtualPath'];
        if (pageCategory && !disableAutoVirtualPath) {
          const pageCategoryForUrl = String(pageCategory)
            .replace(/\s+page\s*$/i, '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-');
          const pageUrl = `/SRP/${pageCategoryForUrl}`;
          this.analyticsService.trackVirtualPathUpdate(pageUrl, String(pageCategory));
        }

        this.resetScrollPosition();
      });
  }

  private resetScrollPosition(): void {
    window.scrollTo(0, 0);

    // The app uses an internal scroll container for page content.
    const contentElement = document.querySelector<HTMLElement>('.content');
    if (contentElement) {
      contentElement.scrollTop = 0;
      contentElement.scrollLeft = 0;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.gigyaSessionService.stopMonitoring();
  }
}